import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { BRAND, WHATSAPP_LINK, IMG, TRUST_STRIP } from "@/lib/content";
import BookButton from "@/components/layout/BookButton";

export default function Hero() {
  return (
    <section
      data-testid="home-hero"
      className="relative pt-36 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Decorative warm background */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 60% at 80% 10%, rgba(215,20,26,0.10) 0%, transparent 60%), radial-gradient(60% 50% at 10% 90%, rgba(13,27,42,0.05) 0%, transparent 60%), #F8F7F4",
        }}
      />
      <div className="container-sp">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E5E3D8] text-xs text-[#4A5568]"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#D7141A]" />
              <span className="font-medium">Poland-only expertise · 15+ years</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0D1B2A] mt-6 leading-[1.05] tracking-tight"
            >
              Study in Poland with
              <span className="italic text-[#D7141A]"> expert guidance </span>
              you can trust.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.12 }}
              className="mt-6 text-lg text-[#4A5568] max-w-xl leading-relaxed"
            >
              Helping international students choose the right university, complete
              admissions, prepare visa procedures and relocate to Poland — with
              clarity and confidence.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/contact#eligibility" className="btn-primary" data-testid="hero-cta-eligibility">
                Check My Eligibility <ArrowRight className="w-4 h-4" />
              </Link>
              <BookButton track="general" variant="gold" testId="hero-cta-consultation">
                Book Expert Consultation
              </BookButton>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost"
                data-testid="hero-cta-whatsapp"
              >
                <MessageCircle className="w-4 h-4 inline mr-1.5" />
                Ask on WhatsApp
              </a>
            </motion.div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
              {TRUST_STRIP.map((t) => (
                <div
                  key={t}
                  className="text-xs text-[#4A5568] flex items-start gap-2 border-l-2 border-[#D7141A] pl-3 py-1"
                >
                  {t}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative"
            >
              <div className="relative rounded-[2rem] overflow-hidden border border-[#E5E3D8] shadow-[0_30px_80px_rgba(13,27,42,0.15)]">
                <img
                  src={IMG.heroExpert}
                  alt="Poland higher education expert"
                  className="w-full h-[520px] object-cover"
                />
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/55 via-transparent to-transparent"
                />
                <div className="absolute bottom-5 left-5 right-5 bg-white/90 backdrop-blur-xl rounded-2xl p-4 border border-white/60">
                  <div className="overline">{BRAND.expertTitle}</div>
                  <div className="font-serif text-lg text-[#0D1B2A] mt-0.5">{BRAND.expertName}</div>
                  <div className="text-xs text-[#4A5568] mt-1">
                    Trusted by international students and families planning their move to Poland.
                  </div>
                </div>
              </div>

              {/* Floating card — community recognition */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="hidden md:flex absolute -left-8 top-12 bg-white rounded-2xl p-4 shadow-lg border border-[#E5E3D8] w-[230px]"
              >
                <div>
                  <div className="overline text-[10px]">Recognised on</div>
                  <ul className="mt-2 space-y-1.5 text-sm text-[#0D1B2A]">
                    <li>· Facebook · <span className="text-[#1877F2] font-medium">StudyPoland.Info</span></li>
                    <li>· TikTok · <span className="text-[#D7141A] font-medium">@krzysztof_szymanski</span></li>
                    <li>· University fairs & conferences</li>
                    <li>· International student community</li>
                  </ul>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
